import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  LogOut,
  Mail,
  Ticket as TicketIcon,
  Inbox,
  Trash2,
  Plus,
  RefreshCw,
  Building2,
} from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "@/components/ui/tabs";
import ClientesModule from "@/components/admin/ClientesModule";
import CentroCobrosModule from "@/components/admin/CentroCobrosModule";
import GastosModule from "@/components/admin/GastosModule";
import BalanceModule from "@/components/admin/BalanceModule";
import PlanRequestsPanel from "@/components/admin/PlanRequestsPanel";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const msgStatusColor = {
  new: "bg-vteal-light/20 text-vteal-light border-vteal-light/30",
  read: "bg-amber-500/20 text-amber-300 border-amber-500/30",
  resolved: "bg-gray-500/20 text-gray-300 border-gray-500/30",
};
const msgStatusLabel = { new: "Nuevo", read: "Leído", resolved: "Resuelto" };

const ticketStatusColor = {
  abierto: "bg-vteal-light/20 text-vteal-light border-vteal-light/30",
  en_progreso: "bg-amber-500/20 text-amber-300 border-amber-500/30",
  cerrado: "bg-gray-500/20 text-gray-300 border-gray-500/30",
};
const ticketStatusLabel = { abierto: "Abierto", en_progreso: "En progreso", cerrado: "Cerrado" };
const priorityColor = { alta: "text-red-400", media: "text-amber-300", baja: "text-gray-400" };

function StatCard({ icon: Icon, label, value }) {
  return (
    <div className="rounded-2xl bg-vnavy-800 border border-white/10 p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="h-11 w-11 rounded-xl bg-vteal/15 flex items-center justify-center">
          <Icon className="h-5 w-5 text-vteal-light" />
        </div>
      </div>
      <p className="font-serif text-4xl text-white">{value}</p>
      <p className="text-gray-500 text-sm mt-1">{label}</p>
    </div>
  );
}

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({ total_messages: 0, new_messages: 0, total_tickets: 0, open_tickets: 0 });
  const [messages, setMessages] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const [s, m, t] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/messages"),
        api.get("/admin/tickets"),
      ]);
      setStats(s.data);
      setMessages(m.data);
      setTickets(t.data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Error al cargar datos.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const doLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  const updateMsg = async (id, status) => {
    try {
      await api.patch(`/admin/messages/${id}`, { status });
      toast.success("Mensaje actualizado.");
      load();
    } catch {
      toast.error("No se pudo actualizar.");
    }
  };
  const delMsg = async (id) => {
    try {
      await api.delete(`/admin/messages/${id}`);
      toast.success("Mensaje eliminado.");
      load();
    } catch {
      toast.error("No se pudo eliminar.");
    }
  };

  const updateTicket = async (id, patch) => {
    try {
      await api.patch(`/admin/tickets/${id}`, patch);
      load();
    } catch {
      toast.error("No se pudo actualizar el ticket.");
    }
  };
  const delTicket = async (id) => {
    try {
      await api.delete(`/admin/tickets/${id}`);
      toast.success("Ticket eliminado.");
      load();
    } catch {
      toast.error("No se pudo eliminar.");
    }
  };

  const ticketGroups = (() => {
    const map = {};
    tickets.forEach((t) => {
      const k = t.company || "General";
      (map[k] = map[k] || []).push(t);
    });
    return Object.entries(map).sort((a, b) => a[0].localeCompare(b[0]));
  })();

  return (
    <div className="min-h-screen bg-vnavy">
      <header className="sticky top-0 z-40 bg-vnavy/85 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="inline-flex items-center bg-white rounded-lg px-3 py-1.5">
              <img src="/logo-vertice-h.webp" alt="Grupo Vértice CR" className="h-8 w-auto object-contain" />
            </span>
            <div>
              <p className="text-white font-semibold text-sm leading-tight">Panel Administrativo</p>
              <p className="text-gray-500 text-xs">{user?.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              data-testid="refresh-btn"
              onClick={load}
              className="p-2.5 rounded-lg border border-white/10 text-gray-400 hover:text-white hover:border-vteal-light/40 transition-colors"
            >
              <RefreshCw className="h-4 w-4" />
            </button>
            <button
              data-testid="logout-btn"
              onClick={doLogout}
              className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-white/5 border border-white/10 text-sm text-gray-300 hover:text-white hover:border-red-500/40 transition-colors"
            >
              <LogOut className="h-4 w-4" /> Salir
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-10">
        <h1 className="font-serif text-3xl sm:text-4xl text-white mb-8">Resumen General</h1>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-12">
          <StatCard icon={Mail} label="Mensajes totales" value={stats.total_messages} />
          <StatCard icon={Inbox} label="Mensajes nuevos" value={stats.new_messages} />
          <StatCard icon={TicketIcon} label="Tickets totales" value={stats.total_tickets} />
          <StatCard icon={TicketIcon} label="Tickets abiertos" value={stats.open_tickets} />
        </div>

        <Tabs defaultValue="clients">
          <TabsList className="bg-vnavy-800 border border-white/10 flex-wrap h-auto">
            <TabsTrigger value="clients" data-testid="tab-clients" className="data-[state=active]:bg-vteal-light data-[state=active]:text-vnavy">
              Clientes
            </TabsTrigger>
            <TabsTrigger value="plan-requests" data-testid="tab-plan-requests" className="data-[state=active]:bg-vteal-light data-[state=active]:text-vnavy">
              Solicitudes de Planes
            </TabsTrigger>
            <TabsTrigger value="messages" data-testid="tab-messages" className="data-[state=active]:bg-vteal-light data-[state=active]:text-vnavy">
              Mensajes de Contacto
            </TabsTrigger>
            <TabsTrigger value="tickets" data-testid="tab-tickets" className="data-[state=active]:bg-vteal-light data-[state=active]:text-vnavy">
              Tickets de Solicitudes
            </TabsTrigger>
            <TabsTrigger value="payments" data-testid="tab-payments" className="data-[state=active]:bg-vteal-light data-[state=active]:text-vnavy">
              Control de Cobros
            </TabsTrigger>
            <TabsTrigger value="expenses" data-testid="tab-expenses" className="data-[state=active]:bg-vteal-light data-[state=active]:text-vnavy">
              Control de Gastos
            </TabsTrigger>
            <TabsTrigger value="balance" data-testid="tab-balance" className="data-[state=active]:bg-vteal-light data-[state=active]:text-vnavy">
              Balance
            </TabsTrigger>
          </TabsList>

          {/* CLIENTS */}
          <TabsContent value="clients" className="mt-6">
            <ClientesModule />
          </TabsContent>

          {/* PLAN REQUESTS */}
          <TabsContent value="plan-requests" className="mt-6">
            <PlanRequestsPanel />
          </TabsContent>

          {/* MESSAGES */}
          <TabsContent value="messages" className="mt-6">
            {loading ? (
              <p className="text-gray-500 py-12 text-center">Cargando...</p>
            ) : messages.length === 0 ? (
              <div className="text-center py-20 text-gray-500">
                <Inbox className="h-10 w-10 mx-auto mb-3 opacity-40" />
                Aún no hay mensajes de contacto.
              </div>
            ) : (
              <div className="space-y-4" data-testid="messages-list">
                {messages.map((m) => (
                  <div key={m.id} className="rounded-xl bg-vnavy-800 border border-white/10 p-6">
                    <div className="flex flex-wrap items-start justify-between gap-4 mb-3">
                      <div>
                        <div className="flex items-center gap-3 flex-wrap">
                          <h3 className="text-white font-semibold">{m.name}</h3>
                          <Badge className={`border ${msgStatusColor[m.status] || ""}`}>{msgStatusLabel[m.status] || m.status}</Badge>
                          {m.service && <span className="text-xs text-vteal-light">{m.service}</span>}
                        </div>
                        <p className="text-gray-500 text-sm mt-1">
                          {m.email}
                          {m.phone ? ` · ${m.phone}` : ""}
                          {m.company ? ` · ${m.company}` : ""}
                        </p>
                      </div>
                      <span className="text-xs text-gray-600">{new Date(m.created_at).toLocaleString("es-CR")}</span>
                    </div>
                    <p className="text-gray-300 text-sm leading-relaxed mb-4 whitespace-pre-wrap">{m.message}</p>
                    <div className="flex flex-wrap items-center gap-2">
                      <button onClick={() => updateMsg(m.id, "read")} className="text-xs px-3 py-1.5 rounded-lg border border-white/10 text-gray-300 hover:border-amber-400/40 transition-colors">
                        Marcar leído
                      </button>
                      <button onClick={() => updateMsg(m.id, "resolved")} className="text-xs px-3 py-1.5 rounded-lg border border-white/10 text-gray-300 hover:border-vteal-light/40 transition-colors">
                        Marcar resuelto
                      </button>
                      <a href={`mailto:${m.email}`} className="text-xs px-3 py-1.5 rounded-lg bg-vteal-light/15 text-vteal-light hover:bg-vteal-light/25 transition-colors">
                        Responder
                      </a>
                      <button onClick={() => delMsg(m.id)} className="text-xs px-3 py-1.5 rounded-lg border border-white/10 text-red-400 hover:border-red-500/40 transition-colors flex items-center gap-1.5 ml-auto">
                        <Trash2 className="h-3.5 w-3.5" /> Eliminar
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* TICKETS */}
          <TabsContent value="tickets" className="mt-6">
            <div className="flex justify-end mb-5">
              <NewTicketDialog onCreated={load} />
            </div>
            {tickets.length === 0 ? (
              <div className="text-center py-20 text-gray-500">
                <TicketIcon className="h-10 w-10 mx-auto mb-3 opacity-40" />
                No hay tickets registrados.
              </div>
            ) : (
              <div className="space-y-8" data-testid="tickets-list">
                {ticketGroups.map(([company, list]) => (
                  <div key={company}>
                    <div className="flex items-center gap-2 mb-3" data-testid={`ticket-group-${company.toLowerCase().replace(/\s+/g, "-")}`}>
                      <Building2 className="h-4 w-4 text-vteal-light" />
                      <h3 className="text-white font-semibold">{company}</h3>
                      <Badge className="border bg-white/5 text-gray-400 border-white/10">{list.length}</Badge>
                    </div>
                    <div className="space-y-4">
                      {list.map((t) => (
                  <div key={t.id} className="rounded-xl bg-vnavy-800 border border-white/10 p-6">
                    <div className="flex flex-wrap items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 flex-wrap mb-2">
                          <h3 className="text-white font-semibold">{t.title}</h3>
                          <Badge className={`border ${ticketStatusColor[t.status] || ""}`}>{ticketStatusLabel[t.status] || t.status}</Badge>
                          <span className={`text-xs font-semibold uppercase ${priorityColor[t.priority] || ""}`}>{t.priority}</span>
                          <span className="text-xs text-gray-500 uppercase tracking-wide">{t.system}</span>
                        </div>
                        {t.description && <p className="text-gray-400 text-sm leading-relaxed">{t.description}</p>}
                        {t.requester && <p className="text-gray-600 text-xs mt-2">Solicitante: {t.requester}</p>}
                      </div>
                      <div className="flex items-center gap-2">
                        <Select value={t.status} onValueChange={(v) => updateTicket(t.id, { status: v })}>
                          <SelectTrigger className="w-36 h-9 bg-vnavy border-white/15 text-white text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-vnavy-800 border-white/15 text-white">
                            <SelectItem value="abierto">Abierto</SelectItem>
                            <SelectItem value="en_progreso">En progreso</SelectItem>
                            <SelectItem value="cerrado">Cerrado</SelectItem>
                          </SelectContent>
                        </Select>
                        <button onClick={() => delTicket(t.id)} className="p-2 rounded-lg border border-white/10 text-red-400 hover:border-red-500/40 transition-colors">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* PAYMENTS */}
          <TabsContent value="payments" className="mt-6">
            <CentroCobrosModule />
          </TabsContent>

          {/* EXPENSES */}
          <TabsContent value="expenses" className="mt-6">
            <GastosModule />
          </TabsContent>

          {/* BALANCE */}
          <TabsContent value="balance" className="mt-6">
            <BalanceModule />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}

function NewTicketDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", system: "general", priority: "media", requester: "", company: "" });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.title) {
      toast.error("El título es obligatorio.");
      return;
    }
    setSaving(true);
    try {
      await api.post("/admin/tickets", form);
      toast.success("Ticket creado.");
      setForm({ title: "", description: "", system: "general", priority: "media", requester: "", company: "" });
      setOpen(false);
      onCreated();
    } catch {
      toast.error("No se pudo crear el ticket.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid="new-ticket-btn" className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-vteal-light text-vnavy font-semibold text-sm hover:bg-white transition-colors">
          <Plus className="h-4 w-4" /> Nuevo Ticket
        </button>
      </DialogTrigger>
      <DialogContent className="bg-vnavy-800 border-white/10 text-white">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">Nuevo Ticket de Solicitud</DialogTitle>
          <DialogDescription className="text-gray-500">
            Registre una solicitud interna asignándola a un sistema y una prioridad.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <label className="text-sm text-gray-400 mb-2 block">Título *</label>
            <Input data-testid="ticket-title" value={form.title} onChange={(e) => set("title", e.target.value)} className="bg-vnavy border-white/15 text-white focus-visible:ring-vteal-light" />
          </div>
          <div>
            <label className="text-sm text-gray-400 mb-2 block">Descripción</label>
            <Textarea data-testid="ticket-description" value={form.description} onChange={(e) => set("description", e.target.value)} rows={3} className="bg-vnavy border-white/15 text-white focus-visible:ring-vteal-light resize-none" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-gray-400 mb-2 block">Sistema</label>
              <Select value={form.system} onValueChange={(v) => set("system", v)}>
                <SelectTrigger className="bg-vnavy border-white/15 text-white"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-vnavy-800 border-white/15 text-white">
                  <SelectItem value="general">General</SelectItem>
                  <SelectItem value="rrhh">Recursos Humanos</SelectItem>
                  <SelectItem value="legal">Despacho Legal</SelectItem>
                  <SelectItem value="contabilidad">Contabilidad</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm text-gray-400 mb-2 block">Prioridad</label>
              <Select value={form.priority} onValueChange={(v) => set("priority", v)}>
                <SelectTrigger className="bg-vnavy border-white/15 text-white"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-vnavy-800 border-white/15 text-white">
                  <SelectItem value="baja">Baja</SelectItem>
                  <SelectItem value="media">Media</SelectItem>
                  <SelectItem value="alta">Alta</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <label className="text-sm text-gray-400 mb-2 block">Solicitante</label>
            <Input data-testid="ticket-requester" value={form.requester} onChange={(e) => set("requester", e.target.value)} placeholder="Nombre o empresa" className="bg-vnavy border-white/15 text-white focus-visible:ring-vteal-light" />
          </div>
          <div>
            <label className="text-sm text-gray-400 mb-2 block">Empresa (para agrupar)</label>
            <Input data-testid="ticket-company" value={form.company} onChange={(e) => set("company", e.target.value)} placeholder="Nombre de la empresa" className="bg-vnavy border-white/15 text-white focus-visible:ring-vteal-light" />
          </div>
        </div>
        <DialogFooter>
          <button data-testid="ticket-save" onClick={submit} disabled={saving} className="w-full px-6 py-3 rounded-full bg-vteal-light text-vnavy font-semibold hover:bg-white transition-colors disabled:opacity-60">
            {saving ? "Guardando..." : "Crear Ticket"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
